import { Component, computed, inject, signal } from '@angular/core';
import { AuthenticationService } from '../../services/authentication.service';
import { SnackbarService } from '../../services/snackbar.service';
import { ButtonDirective } from '../../components/button/button.directive';
import { Router } from '@angular/router';
import { Route } from '../../utils/route';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InlineLoaderComponent } from '../../components/inline-loader/inline-loader.component';
import { InputComponent } from '../../components/input/input.component';
import { DictionaryService } from '../../services/dictionary.service';
@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    ButtonDirective,
    ReactiveFormsModule,
    InlineLoaderComponent,
    InputComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent extends Route {
  protected dictionary = inject(DictionaryService);
  private authService = inject(AuthenticationService);
  private dictionaryService = inject(DictionaryService);
  private router = inject(Router);
  private snackbarService = inject(SnackbarService);
  /**
   * Reactive form containing login fields and their validators.
   * - email: required + email format
   * - password: required
   */
  loginForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required]),
  });

  /**
   * Tracks whether a submit attempt occurred to allow showing errors
   * before the control is individually touched.
   */
  private submitted = false;

  /** Local loading state for this page */
  isLoading = false;

  get email() {
    return this.loginForm.controls.email.value;
  }
  get password() {
    return this.loginForm.controls.password.value;
  }

  /**
   * True when email control is invalid and either touched or a submit attempt happened.
   */
  get emailInvalid() {
    const control = this.loginForm.controls.email as FormControl;
    return control.invalid && (control.touched || this.submitted);
  }

  /**
   * True when the email control fails the required validator.
   */
  get emailRequired() {
    const control = this.loginForm.controls.email as FormControl;
    return !!control.errors?.['required'];
  }
  /**
   * True when the email control fails the Angular built-in email validator.
   */
  get emailFormatInvalid() {
    const control = this.loginForm.controls.email as FormControl;
    return !!control.errors?.['email'];
  }

  /**
   * True when password control is invalid and either touched or a submit attempt happened.
   */
  get passwordInvalid() {
    const control = this.loginForm.controls.password as FormControl;
    return control.invalid && (control.touched || this.submitted);
  }

  /**
   * @description Logs User In
   */
  login = async () => {
    this.submitted = true;
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.loginForm.disable({ emitEvent: false });
    try {
      const loginData = await this.authService.login({
        email: this.email as string,
        password: this.password as string,
      });

      // Show welcome message
      this.snackbarService.showSuccess(`Welcome back, ${loginData.user.name || 'User'}!`, {
        duration: 4000
      });

      // Redirect to dashboard after successful login
      const permittedRoutes = this.authService.currentUserPolicy?.menuItems!;
      this.router.navigate([permittedRoutes[0]]);
    } catch (error) {
      // Show error message using snackbar
      this.snackbarService.showError('Invalid email or password', {
        duration: 5000
      });
      this.loginForm.setErrors({ invalid: true });
    } finally {
      this.loginForm.enable({ emitEvent: false });
      this.isLoading = false;
    }
  };
}
